import type {
  BooksChoice,
  QuestionnaireUserChoices,
  SummaryRow,
} from './onboarding.types'

export const ONBOARDING_BOOKS_BY_SUBJECT = {
  'Getting ahead professionally': [
    'the-million-dollar-one-person-business',
    'what-the-most-successful-people-do-before-breakfast',
    'never-split-the-difference',
    'whats-your-dream',
    'principles-life-and-work',
  ],
  'Becoming a better partner or parent': [
    '8-rules-of-love',
    'attached',
    'nonviolent-communication',
    'set-boundaries-find-peace',
    'red-flags-green-flags',
  ],
  'Taking control of my finances': [
    '1929',
    'the-psychology-of-money',
    'rich-dad-poor-dad',
    'the-5-types-of-wealth',
    'the-intelligent-investor',
  ],
  'Being more focused and productive': [
    'elastic-habits',
    'what-the-most-successful-people-do-before-breakfast',
    'badass-habits',
    'stress-less-accomplish-more',
    'how-to-calm-your-mind',
  ],
  'Improving my health and wellness': [
    'meditations',
    'healing-spaces',
    'mind-your-body',
    'the-body-keeps-the-score',
    'dopamine-nation',
  ],
  'Staying curious and continuing to learn': [
    'the-million-dollar-one-person-business',
    '8-rules-of-love',
    '1929',
    'elastic-habits',
    'meditations',
  ],
} as const satisfies Record<string, string[]>

export const ONBOARDING_BOOKS_SUBTITLE_BY_SUBJECT = {
  'Getting ahead professionally': 'get ahead in your career',
  'Becoming a better partner or parent': 'become a better partner',
  'Taking control of my finances': 'take control of your finances',
  'Being more focused and productive': 'stay focused and productive',
  'Improving my health and wellness': 'improve your health and wellness',
  'Staying curious and continuing to learn': 'keep learning and stay curious',
} as const satisfies Record<keyof typeof ONBOARDING_BOOKS_BY_SUBJECT, string>

export type OnboardingSubject = keyof typeof ONBOARDING_BOOKS_BY_SUBJECT

/** Fallback when screen 2 has no saved choice. */
export const ONBOARDING_DEFAULT_SUBJECT =
  'Staying curious and continuing to learn' as const satisfies OnboardingSubject

/** Book category tags that map to each onboarding subject. */
export const ONBOARDING_BOOK_TAGS_BY_SUBJECT = {
  'Getting ahead professionally': [
    'Business',
    'Career/Success',
    'Economics',
    'Entrepreneurship',
    'Marketing',
    'Management/Leadership',
    'Sales',
    'Technology',
  ],
  'Taking control of my finances': ['Money/Finance'],
  'Becoming a better partner or parent': [
    'Communication',
    'Parenting',
    'Relationships',
  ],
  'Being more focused and productive': [
    'Motivation',
    'Productivity',
    'Psychology',
    'Self-Improvement',
  ],
  'Improving my health and wellness': [
    'Food',
    'Health',
    'Lifestyle',
    'Spirituality',
    'Sports',
  ],
  'Staying curious and continuing to learn': [
    'Arts/Design',
    'Biography/Memoir',
    'Education',
    'Entertainment',
    'Fiction',
    'History',
    'Law',
    'Media',
    'News',
    'Philosophy',
    'Politics',
    'Science',
    'Society/Culture',
  ],
} as const satisfies Record<OnboardingSubject, readonly string[]>

const ONBOARDING_SUBJECT_BY_BOOK_TAG = Object.fromEntries(
  Object.entries(ONBOARDING_BOOK_TAGS_BY_SUBJECT).flatMap(([subject, tags]) =>
    tags.map((tag) => [tag, subject]),
  ),
) as Record<string, OnboardingSubject>

/** Map a book's category tags to an onboarding subject. */
export function subjectFromBookTags(tags: string[]): OnboardingSubject {
  for (const tag of tags) {
    const subject = ONBOARDING_SUBJECT_BY_BOOK_TAG[tag]
    if (subject) {
      return subject
    }
  }
  return ONBOARDING_DEFAULT_SUBJECT
}

export const ONBOARDING_WORKING_TOWARDS_BY_SUBJECT = {
  'Getting ahead professionally': [
    'A promotion or new role',
    'Stronger leadership skills',
    'Starting or growing a business',
    'Negotiation and influence',
    'Something else in my career',
  ],
  'Becoming a better partner or parent': [
    'Healthier communication',
    'Setting boundaries with care',
    'Parenting with more confidence',
    'Deepening trust in relationships',
    'Something else in relationships',
  ],
  'Taking control of my finances': [
    'Building long-term wealth',
    'Budgeting and saving habits',
    'Investing with more clarity',
    'Understanding money psychology',
    'Something else financially',
  ],
  'Being more focused and productive': [
    'Better habits and routines',
    'Managing stress and energy',
    'Focus and deep work',
    'Getting more done with less effort',
    'Something else around productivity',
  ],
  'Improving my health and wellness': [
    'Mental health and resilience',
    'Sleep, energy, and recovery',
    'Body and movement',
    'Mindfulness and calm',
    'Something else for wellness',
  ],
  'Staying curious and continuing to learn': [
    'Big ideas across disciplines',
    'History, science, or culture',
    'Skills for a side project',
    'Better retention and note-taking',
    'Something else I want to explore',
  ],
} as const satisfies Record<OnboardingSubject, readonly string[]>

/*
  Get the book slugs shown on the personalized onboarding books page.
  @param subject - Subject chosen earlier in the questionnaire.
  @returns URL slugs for that subject
*/
export function getOnboardingBooks(subject: OnboardingSubject): string[] {
  return ONBOARDING_BOOKS_BY_SUBJECT[subject]
}

/*
  Get subtitle for the PersonalizedBooks page, based on the subject from screen 2.
  @param subject - Subject chosen earlier in the questionnaire.
  @returns Phrase inserted after "To help you"
*/
export function getOnboardingBooksSubtitle(subject: OnboardingSubject): string {
  return ONBOARDING_BOOKS_SUBTITLE_BY_SUBJECT[subject]
}

/**
 * Skip when there is nothing to show: no mapped summary rows and no books the user liked.
 */
export function shouldSkipLearningSetupSummary(
  choices: QuestionnaireUserChoices,
): boolean {
  return (
    buildLearningSetupSummaryRows(choices).length === 0 &&
    getLikedBookSlugsFromUserChoices(choices).length === 0
  )
}

export function getLikedBookSlugsFromUserChoices(
  choices: QuestionnaireUserChoices,
): string[] {
  const selectedBooks = choices['personalized-book-selection'] as BooksChoice
  if (!selectedBooks) {
    return []
  }
  return positiveScoreBookSlugsFromBooksChoice(selectedBooks)
}

export function positiveScoreBookSlugsFromBooksChoice(
  booksChoice: BooksChoice,
): string[] {
  return booksChoice.filter((b) => b.score > 0).map((b) => b.id)
}

const BENEFIT_BY_OPTION_LABEL: Record<string, string> = {
  'Getting ahead professionally':
    "We'll help you stay consistent and think more strategically.",
  'Becoming a better partner or parent':
    "We'll surface ideas you can use in real conversations.",
  'Taking control of my finances':
    "We'll keep money concepts practical and actionable.",
  'Being more focused and productive':
    "We'll help you build momentum without the overwhelm.",
  'Improving my health and wellness':
    "We'll balance depth with habits you can sustain.",
  'Staying curious and continuing to learn':
    "We'll keep things fresh so learning stays interesting.",

  'My schedule is a circus':
    "We'll help you stay consistent and thinking more strategically.",
  'Reading puts me to sleep':
    "We'll mix formats so reading feels lighter and stickier.",
  'Nothing grabs my attention':
    "We'll lead with hooks and ideas that feel immediately relevant.",
  'I prefer other forms of media':
    "We'll meet you with listenable and skimmable options too.",
  "I'm already a reading legend":
    "We'll respect your pace and go deeper where you want it.",

  Read: "We'll prioritize clear, scannable summaries you can revisit fast.",
  Listen: "We'll make it easy to learn when you're on the move.",
  'Community-based exercises':
    "We'll highlight practical exercises you can try in small steps.",
  'A bit of each':
    "We'll blend reading, listening, and practice so nothing feels one-note.",

  '30 minutes a day':
    "We'll pace recommendations so they fit a fuller daily block.",
  '5 minutes a day':
    "We'll keep wins small and repeatable so the habit sticks.",
  '1 hour or more': "We'll help you go deeper when you have room to explore.",
  '15 minutes a day':
    "We'll keep sessions tight and satisfying in short windows.",

  Morning: "We'll align nudges with a fresh start to your day.",
  Midday: "We'll help you recharge learning during natural breaks.",
  Evening: "We'll keep wind-down learning calm and low-friction.",

  'Personal growth on my own time':
    "We'll keep the experience focused on your personal goals.",
  'Academic reading for school':
    "We'll help you retain more for classes and deadlines.",
  'My company set this up for me':
    "We'll keep professional context in mind as you learn.",

  'A promotion or new role':
    "We'll emphasize skills and ideas that translate at work.",
  'Stronger leadership skills':
    "We'll reinforce leadership patterns you can apply quickly.",
  'Starting or growing a business':
    "We'll favor practical moves over abstract theory.",
  'Negotiation and influence':
    "We'll sharpen frameworks you can use in real conversations.",
  'Something else in my career':
    "We'll keep career growth flexible around your specifics.",
  'Healthier communication':
    "We'll give you language and frameworks you can try today.",
  'Setting boundaries with care':
    "We'll support clarity without guilt in tough moments.",
  'Parenting with more confidence':
    "We'll offer ideas sized for busy family life.",
  'Deepening trust in relationships':
    "We'll focus on small shifts that rebuild connection.",
  'Something else in relationships':
    "We'll keep relationship growth grounded in your reality.",
  'Building long-term wealth':
    "We'll connect money ideas to long-horizon decisions.",
  'Budgeting and saving habits':
    "We'll keep money advice practical week to week.",
  'Investing with more clarity':
    "We'll demystify investing concepts step by step.",
  'Understanding money psychology':
    "We'll link behavior and emotions to better money moves.",
  'Something else financially':
    "We'll personalize money guidance around your goals.",
  'Better habits and routines':
    "We'll help you stack habits that actually stick.",
  'Managing stress and energy':
    "We'll balance productivity with recovery you can feel.",
  'Focus and deep work': "We'll protect attention with ideas worth the focus.",
  'Getting more done with less effort': "We'll highlight leverage over hustle.",
  'Something else around productivity':
    "We'll adapt productivity ideas to how you work best.",
  'Mental health and resilience':
    "We'll keep mental health insights compassionate and usable.",
  'Sleep, energy, and recovery':
    "We'll tie learning to rhythms that support your energy.",
  'Body and movement': "We'll connect ideas to sustainable movement and care.",
  'Mindfulness and calm': "We'll favor calming practices you can repeat daily.",
  'Something else for wellness':
    "We'll keep wellness guidance flexible to your needs.",
  'Big ideas across disciplines':
    "We'll connect dots across fields so it stays fascinating.",
  'History, science, or culture':
    "We'll feed curiosity with rich, well-framed summaries.",
  'Skills for a side project':
    "We'll prioritize skills you can apply on side projects fast.",
  'Better retention and note-taking':
    "We'll reinforce formats that improve recall and notes.",
  'Something else I want to explore':
    "We'll keep exploration wide without losing focus.",

  'Podcast summaries':
    "We'll highlight listen-friendly summaries when you're on the go.",
  'Article summaries':
    "We'll weave in article summaries for quicker hits of insight.",
  "I'm interested in both":
    "You'll get books, podcasts, and articles working together in one place.",
}

const LEARNING_SETUP_SUMMARY_ROW_SOURCES = [
  { sectionLabel: 'Your focus', choiceKey: 'what-subject-interests-you' },
  {
    sectionLabel: 'Working towards',
    choiceKey: 'what-are-you-working-towards',
  },
  { sectionLabel: 'Main challenge', choiceKey: 'what-gets-in-your-way' },
  { sectionLabel: 'Learning way', choiceKey: 'how-do-you-learn' },
  { sectionLabel: 'Daily commitment', choiceKey: 'how-much-time' },
  {
    sectionLabel: 'Best time for studying',
    choiceKey: 'what-time-of-the-day',
  },
  { sectionLabel: 'Extra formats', choiceKey: 'does-this-sound-interesting' },
] as const

function shouldOmitSummaryChoice(value: unknown): boolean {
  return typeof value !== 'string' || value.length === 0
}

/**
 * Rows for the summary card
 */
export function buildLearningSetupSummaryRows(
  choices: QuestionnaireUserChoices,
): SummaryRow[] {
  return LEARNING_SETUP_SUMMARY_ROW_SOURCES.map(
    ({ sectionLabel, choiceKey }): SummaryRow | null => {
      const choice = choices[choiceKey]
      if (shouldOmitSummaryChoice(choice)) {
        return null
      }
      const text = choice as string

      return {
        sectionLabel,
        quotedChoice: text,
        benefitLine: BENEFIT_BY_OPTION_LABEL[text],
      }
    },
  ).filter((row) => row !== null) as SummaryRow[]
}
